import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaChevronLeft, FaChevronRight, FaPlus, FaVideo,
    FaBook, FaLeaf, FaUsers, FaLock, FaUserCircle,
    FaTimes, FaStar, FaBell
} from 'react-icons/fa';
import axios from 'axios';
import './PersonalAgenda.css';

// ── MOCK EVENTS ────────────────────────────────────────────────────────────
const AGENDA_EVENTS = [
    {
        id: 1,
        day: 2,
        month: 4, // 0-indexed (Mayo = 4)
        year: 2024,
        label: 'Webinar Neuro...',
        title: 'Neurociencia Computacional',
        category: 'ACADÉMICO',
        time: '14:00 - 15:30',
        type: 'Webinar',
        color: '#22c55e',
        speaker: 'Dr. Carlos Méndez',
        action: 'join',
    },
    {
        id: 2,
        day: 7,
        month: 4,
        year: 2024,
        label: 'Lab Report',
        title: 'Entrega Lab Report — Bioquímica',
        category: 'ACADÉMICO',
        time: 'Jueves, 23:59',
        type: 'Fecha Límite',
        color: '#3b82f6',
        project: 'Laboratorio Molecular Q2',
        action: 'upload',
    },
    {
        id: 3,
        day: 9,
        month: 4,
        year: 2024,
        label: 'Mentoría IA',
        title: 'Revisión de Tesis: Sostenibilidad',
        category: 'ACADÉMICO',
        time: 'Mañana, 09:00',
        type: 'Mentoría',
        color: '#a855f7',
        speaker: 'Dra. Elena Rius',
        action: 'confirm',
    },
    {
        id: 4,
        day: 2,
        month: 4,
        year: 2024,
        label: 'Microbioma Web',
        title: 'Microbioma y Salud Planetaria',
        category: 'COMUNIDAD',
        time: '14:00 - 15:30',
        type: 'Webinar',
        color: '#22c55e',
        speaker: 'Dr. Carlos Méndez',
        action: 'join',
    },
    {
        id: 5,
        day: 15,
        month: 4,
        year: 2024,
        label: 'Entrega Propuesta',
        title: 'Entrega Propuesta Investigación',
        category: 'SOSTENIBILIDAD',
        time: 'Viernes, 23:59',
        type: 'Fecha Límite',
        color: '#FF6600',
        project: 'Urbanismo para la Salud',
        action: 'upload',
    },
    {
        id: 6,
        day: 22,
        month: 4,
        year: 2024,
        label: 'Simposio IA',
        title: 'Simposio IA Aplicada a Biotecnología',
        category: 'ACADÉMICO',
        time: '10:00 - 18:00',
        type: 'Congreso',
        color: '#FF6600',
        location: 'Madrid, España',
        action: 'join',
    },
    {
        id: 7,
        day: 28,
        month: 4,
        year: 2024,
        label: 'Grupo Eco',
        title: 'Reunión Grupo Eco-Innovación',
        category: 'COMUNIDAD',
        time: '16:30 - 17:30',
        type: 'Comunidad',
        color: '#0ea5e9',
        speaker: 'Coord. Laura Pérez',
        action: 'join',
    },
];

// Upcoming events for the right panel (ordered by urgency)
const UPCOMING_MOCK = [AGENDA_EVENTS[3], AGENDA_EVENTS[2], AGENDA_EVENTS[4]];

// Helper to parse DB events into Agenda events
const parseDbEvent = (e) => {
    // Attempt to extract date
    let day = 1, month = 4, year = 2024; // fallback
    const dateStr = (e.fecha_inicio || e.date || '').toLowerCase();
    
    if (dateStr) {
        // try to parse "15 de Octubre de 2026"
        const dayMatch = dateStr.match(/\b(\d{1,2})\b/);
        const yearMatch = dateStr.match(/\b(20\d{2})\b/);
        
        if (dayMatch) day = parseInt(dayMatch[1], 10);
        if (yearMatch) year = parseInt(yearMatch[1], 10);
        
        const MONTHS_LOWER = [
            'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
            'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december',
            'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
        ];
        
        for (let i = 0; i < MONTHS_LOWER.length; i++) {
            if (dateStr.includes(MONTHS_LOWER[i])) {
                month = i % 12;
                break;
            }
        }
    }
    
    let typeDisplay = e.tipo_evento || e.type || 'Evento';
    if (Array.isArray(typeDisplay)) typeDisplay = typeDisplay[0] || 'Evento';
    typeDisplay = typeDisplay.charAt(0).toUpperCase() + typeDisplay.slice(1);
    
    return {
        id: 'db-' + e.id,
        day,
        month,
        year,
        label: (e.nombre || e.title).substring(0, 15) + '...',
        title: e.nombre || e.title,
        category: 'ACADÉMICO',
        time: 'TBA',
        type: typeDisplay,
        color: '#22c55e',
        speaker: e.organizacion || '',
        action: 'join',
        link: e.link
    };
};

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const WEEKDAYS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
const CATEGORIES = ['TODOS', 'ACADÉMICO', 'COMUNIDAD', 'SOSTENIBILIDAD'];

const CAT_COLORS = {
    ACADÉMICO: '#22c55e',
    COMUNIDAD: '#a855f7',
    SOSTENIBILIDAD: '#0ea5e9',
};

const TYPE_LABELS = {
    Webinar: 'WEBINAR',
    Mentoría: 'MENTORÍA',
    'Fecha Límite': 'FECHA LÍMITE',
    Congreso: 'CONGRESO',
    Comunidad: 'COMUNIDAD',
};

const TYPE_COLORS = {
    Webinar: '#22c55e',
    Mentoría: '#a855f7',
    'Fecha Límite': '#f59e0b',
    Congreso: '#FF6600',
    Comunidad: '#0ea5e9',
};

// ── Calendar ───────────────────────────────────────────────────────────────
const MonthCalendar = ({ year, month, events, onPrev, onNext, onEventClick }) => {
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const cells = Array(startOffset).fill(null).concat(
        Array.from({ length: daysInMonth }, (_, i) => i + 1)
    );
    // Pad to full weeks
    while (cells.length % 7 !== 0) cells.push(null);

    // Build events-per-day map
    const eventMap = {};
    events.forEach(ev => {
        if (ev.month === month && ev.year === year) {
            if (!eventMap[ev.day]) eventMap[ev.day] = [];
            eventMap[ev.day].push(ev);
        }
    });

    const today = new Date(); 

    return (
        <div className="pa-calendar">
            <div className="pa-cal-header">
                <button className="pa-cal-nav" onClick={onPrev}><FaChevronLeft /></button>
                <h2 className="pa-cal-title">{MONTHS[month]} {year}</h2>
                <button className="pa-cal-nav" onClick={onNext}><FaChevronRight /></button>
            </div>

            <div className="pa-cal-grid">
                {WEEKDAYS.map(d => (
                    <div key={d} className="pa-cal-dow">{d}</div>
                ))}
                {cells.map((day, i) => {
                    const dayEvents = day ? (eventMap[day] || []) : [];
                    const isToday = day && day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                    return (
                        <div
                            key={i}
                            className={`pa-cal-cell ${!day ? 'empty' : ''} ${isToday ? 'today' : ''}`}
                        >
                            {day && (
                                <>
                                    <span className="pa-cal-day-num">{day}</span>
                                    <div className="pa-cal-events">
                                        {dayEvents.slice(0, 2).map(ev => (
                                            <span
                                                key={ev.id}
                                                className="pa-cal-event-pill"
                                                onClick={() => onEventClick && onEventClick(ev.id)}
                                                style={{ background: ev.color + '30', borderLeft: `3px solid ${ev.color}`, color: ev.color, cursor: 'pointer' }}
                                            >
                                                {ev.label}
                                            </span>
                                        ))}
                                        {dayEvents.length > 2 && (
                                            <span className="pa-cal-more">+{dayEvents.length - 2}</span>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Week Calendar ──────────────────────────────────────────────────────────
const WeekCalendar = ({ year, month, events, onEventClick }) => {
    // Show first week of the current month for demo
    const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const days = Array.from({ length: 7 }, (_, i) => {
        const date = 1 - startOffset + i;
        return date > 0 ? date : null;
    });

    const eventMap = {};
    events.forEach(ev => {
        if (ev.month === month && ev.year === year) {
            const dow = new Date(year, month, ev.day).getDay();
            const key = (dow === 0 ? 6 : dow - 1); // 0=Mon
            if (!eventMap[key]) eventMap[key] = [];
            eventMap[key].push(ev);
        }
    });

    return (
        <div className="pa-week-calendar">
            <div className="pa-week-header">
                {WEEKDAYS.map((d, i) => (
                    <div key={d} className="pa-week-dow">
                        <span className="pa-week-dow-label">{d}</span>
                        {days[i] && <span className="pa-week-dow-num">{days[i]}</span>}
                    </div>
                ))}
            </div>
            <div className="pa-week-body">
                <div className="pa-week-time-col">
                    {HOURS.map(h => (
                        <div key={h} className="pa-week-hour">{h}:00</div>
                    ))}
                </div>
                {Array.from({ length: 7 }, (_, col) => (
                    <div key={col} className="pa-week-col">
                        {(eventMap[col] || []).slice(0, 1).map(ev => (
                            <div
                                key={ev.id}
                                className="pa-week-event"
                                onClick={() => onEventClick && onEventClick(ev.id)}
                                style={{ background: ev.color + '25', borderLeft: `3px solid ${ev.color}`, color: ev.color, cursor: 'pointer' }}
                            >
                                <span className="pa-week-event-title">{ev.label}</span>
                                <span className="pa-week-event-time">{ev.time?.split(' - ')[0]}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Upcoming Event Card ────────────────────────────────────────────────────
const UpcomingCard = ({ event, highlighted }) => {
    const typeColor = TYPE_COLORS[event.type] || '#FF6600';
    const typeLabel = TYPE_LABELS[event.type] || event.type.toUpperCase();

    return (
        <div 
            id={`event-card-${event.id}`}
            className="pa-upcoming-card" 
            style={highlighted ? { border: `2px solid ${typeColor}`, transform: 'scale(1.02)', transition: 'all 0.3s' } : { transition: 'all 0.3s' }}
        >
            <div className="pa-upcoming-meta">
                <span className="pa-upcoming-type-badge" style={{ color: typeColor, borderColor: typeColor }}>
                    {typeLabel}
                </span>
                <span className="pa-upcoming-time">{event.time}</span>
            </div>
            <h4 className="pa-upcoming-title">{event.title}</h4>
            {event.speaker && <p className="pa-upcoming-sub">Ponente: {event.speaker}</p>}
            {event.project && <p className="pa-upcoming-sub">Proyecto: {event.project}</p>}

            <div className="pa-upcoming-actions">
                {event.link ? (
                    <a href={event.link} target="_blank" rel="noopener noreferrer" className="pa-upcoming-btn outline-full" style={{textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}>
                        <FaVideo /> Ver en plataforma
                    </a>
                ) : event.action === 'join' ? (
                    <button className="pa-upcoming-btn outline-full">
                        <FaVideo /> Unirse a la sesión
                    </button>
                ) : null}
                {event.action === 'confirm' && (
                    <>
                        <button className="pa-upcoming-btn outline">Materiales</button>
                        <button className="pa-upcoming-btn outline">Confirmar</button>
                    </>
                )}
                {event.action === 'upload' && (
                    <button className="pa-upcoming-btn solid">
                        <FaLock /> Subir documentos
                    </button>
                )}
            </div>
        </div>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────
const PersonalAgenda = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState('TODOS');
    const [calView, setCalView] = useState('Mes');
    const [calDate, setCalDate] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
    const [showModal, setShowModal] = useState(false);
    const [reminderAdded, setReminderAdded] = useState(false);
    const [dbAgendaEvents, setDbAgendaEvents] = useState([]);
    const [focusedEventId, setFocusedEventId] = useState(null);

    React.useEffect(() => {
        if (focusedEventId) {
            const container = document.getElementById('pa-upcoming-list-container');
            const el = document.getElementById(`event-card-${focusedEventId}`);
            if (container && el) {
                // Scroll container without moving the whole page
                container.scrollTo({
                    top: el.offsetTop - container.offsetTop - 10,
                    behavior: 'smooth'
                });
            } else if (el) {
                // fallback if container not found
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            // Remove highlight after a few seconds
            const timer = setTimeout(() => setFocusedEventId(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [focusedEventId]);

    React.useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get('http://localhost:5000/api/events', {
                    params: { limit: 100 }
                });
                if (response.data && response.data.events) {
                    const parsed = response.data.events.map(parseDbEvent);
                    setDbAgendaEvents(parsed);
                }
            } catch (err) {
                console.error("Error fetching agenda events", err);
            }
        };
        fetchEvents();
    }, []);

    const allEvents = dbAgendaEvents.length > 0 ? dbAgendaEvents : AGENDA_EVENTS;

    const filteredEvents = useMemo(() => {
        let evs = allEvents;
        if (activeCategory !== 'TODOS') {
            evs = evs.filter(e => e.category === activeCategory);
        }
        return evs;
    }, [activeCategory, allEvents]);

    // Only show events for the currently viewed month on the right list
    const monthEvents = useMemo(() => {
        return filteredEvents.filter(e => e.month === calDate.month && e.year === calDate.year);
    }, [filteredEvents, calDate]);

    const prevMonth = () => setCalDate(d => {
        const m = d.month === 0 ? 11 : d.month - 1;
        const y = d.month === 0 ? d.year - 1 : d.year;
        return { year: y, month: m };
    });

    const nextMonth = () => setCalDate(d => {
        const m = d.month === 11 ? 0 : d.month + 1;
        const y = d.month === 11 ? d.year + 1 : d.year;
        return { year: y, month: m };
    });

    return (
        <div className="pa-page">
            {/* ── HEADER ── */}
            <header className="pa-header">
                <div className="pa-header-inner">
                    <Link to="/" className="pa-logo">
                        <img src="/logoecseia.png" alt="Logo" className="pa-logo-img" />
                        <span className="pa-logo-eco">Eco</span>
                        <span className="pa-logo-rest">ScienceInnova</span>
                    </Link>
                    <nav className="pa-header-nav">
                        <Link to="/" className="pa-nav-link">Inicio</Link>
                        <Link to="/community" className="pa-nav-link">Comunidad</Link>
                        <Link to="/researcher-library" className="pa-nav-link">Recursos</Link>
                        <span className="pa-nav-link active">Mi Agenda</span>
                        <a href="#contacto" className="pa-nav-link">Contacto</a>
                    </nav>
                    <div className="pa-header-right">
                        <button className="pa-nuevo-btn" onClick={() => setShowModal(true)}>
                            <FaPlus /> Nuevo Evento
                        </button>
                        <div className="pa-avatar"><FaUserCircle /></div>
                    </div>
                </div>
            </header>

            {/* ── TITLE + FILTERS ── */}
            <div className="pa-title-area">
                <div className="pa-title-inner">
                    <div>
                        <h1 className="pa-page-title">Mi Agenda Personalizada</h1>
                        <p className="pa-page-subtitle">Gestiona tus investigaciones, mentorías y formación continua.</p>
                    </div>
                    <div className="pa-category-tabs">
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat}
                                className={`pa-cat-tab ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat !== 'TODOS' && (
                                    <span className="pa-cat-dot" style={{ background: CAT_COLORS[cat] }} />
                                )}
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── MAIN BODY ── */}
            <div className="pa-body">
                {/* Left: Calendar */}
                <div className="pa-cal-area">
                    {/* View toggle */}
                    <div className="pa-view-toggle">
                        <button
                            className={`pa-view-btn ${calView === 'Mes' ? 'active' : ''}`}
                            onClick={() => setCalView('Mes')}
                        >
                            Mes
                        </button>
                        <button
                            className={`pa-view-btn ${calView === 'Semana' ? 'active' : ''}`}
                            onClick={() => setCalView('Semana')}
                        >
                            Semana
                        </button>
                    </div>

                    {calView === 'Mes' ? (
                        <MonthCalendar
                            year={calDate.year}
                            month={calDate.month}
                            events={filteredEvents}
                            onPrev={prevMonth}
                            onNext={nextMonth}
                            onEventClick={(id) => setFocusedEventId(id)}
                        />
                    ) : (
                        <WeekCalendar
                            year={calDate.year}
                            month={calDate.month}
                            events={filteredEvents}
                            onEventClick={(id) => setFocusedEventId(id)}
                        />
                    )}

                    {/* IA Suggestion Banner */}
                    <div className="pa-ia-banner">
                        <div className="pa-ia-icon"><FaStar /></div>
                        <div className="pa-ia-content">
                            <p className="pa-ia-title">Sugerencia de la IA: Espacio libre detectado</p>
                            <p className="pa-ia-msg">
                                Tienes 2 horas libres el martes. ¿Quieres programar una sesión sobre{' '}
                                <span className="pa-ia-highlight">"Gestión de Residuos Hospitalarios"</span>?
                            </p>
                        </div>
                        <button className="pa-ia-btn" onClick={() => setShowModal(true)}>
                            Programar ahora
                        </button>
                    </div>
                </div>

                {/* Right: Upcoming events */}
                <aside className="pa-right">
                    <div className="pa-upcoming-section">
                        <h3 className="pa-upcoming-heading">
                            <FaBell className="pa-upcoming-icon" /> Eventos del Mes
                        </h3>

                        <div id="pa-upcoming-list-container" className="pa-upcoming-list-scroll" style={{ flexGrow: 1, overflowY: 'auto', paddingRight: '12px', display: 'flex', flexDirection: 'column', gap: '1rem', minHeight: '300px' }}>
                            {monthEvents.length === 0 ? (
                                <p style={{ color: '#888', fontSize: '0.9rem', textAlign: 'center', marginTop: '2rem' }}>
                                    No hay eventos para este mes.
                                </p>
                            ) : (
                                monthEvents.map(ev => (
                                    <UpcomingCard key={ev.id} event={ev} highlighted={focusedEventId === ev.id} />
                                ))
                            )}
                        </div>

                        {/* Añadir recordatorio */}
                        {!reminderAdded ? (
                            <button
                                className="pa-add-reminder"
                                onClick={() => setReminderAdded(true)}
                            >
                                <div className="pa-add-reminder-plus"><FaPlus /></div>
                                <span>AÑADIR RECORDATORIO</span>
                            </button>
                        ) : (
                            <div className="pa-reminder-added">
                                ✅ Recordatorio añadido
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            {/* ── FOOTER ── */}
            <footer className="pa-footer">
                <div className="pa-footer-inner">
                    <Link to="/" className="pa-footer-logo">
                        <img src="/logoecseia.png" alt="Logo" className="pa-footer-logo-img" />
                        <span><span className="pa-logo-eco">Eco</span>ScienceInnova</span>
                    </Link>
                    <p className="pa-footer-copy">© 2024 EcoScienceInnova. Plataforma de planificación científica.</p>
                    <div className="pa-footer-links">
                        <a href="#ayuda">Ayuda</a>
                        <a href="#config">Configuración</a>
                        <a href="#privacidad">Privacidad</a>
                    </div>
                </div>
            </footer>

            {/* ── NEW EVENT MODAL ── */}
            {showModal && (
                <div className="pa-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="pa-modal" onClick={e => e.stopPropagation()}>
                        <div className="pa-modal-header">
                            <h3>Nuevo Evento</h3>
                            <button className="pa-modal-close" onClick={() => setShowModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="pa-modal-body">
                            <div className="pa-field">
                                <label>Título del evento</label>
                                <input type="text" placeholder="Ej. Revisión de tesis..." className="pa-input" />
                            </div>
                            <div className="pa-field-row">
                                <div className="pa-field">
                                    <label>Fecha</label>
                                    <input type="date" className="pa-input" defaultValue="2024-05-10" />
                                </div>
                                <div className="pa-field">
                                    <label>Hora</label>
                                    <input type="time" className="pa-input" defaultValue="09:00" />
                                </div>
                            </div>
                            <div className="pa-field">
                                <label>Tipo</label>
                                <select className="pa-input">
                                    <option>Webinar</option>
                                    <option>Mentoría</option>
                                    <option>Fecha Límite</option>
                                    <option>Congreso</option>
                                    <option>Comunidad</option>
                                </select>
                            </div>
                            <div className="pa-field">
                                <label>Categoría</label>
                                <select className="pa-input">
                                    <option>ACADÉMICO</option>
                                    <option>COMUNIDAD</option>
                                    <option>SOSTENIBILIDAD</option>
                                </select>
                            </div>
                            <div className="pa-field">
                                <label>Notas</label>
                                <textarea className="pa-input" rows="3" placeholder="Descripción o notas adicionales..." />
                            </div>
                        </div>
                        <div className="pa-modal-footer">
                            <button className="pa-modal-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="pa-modal-save" onClick={() => setShowModal(false)}>Guardar evento</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalAgenda;
